import { z } from "zod";

import fetcher from "@/lib/fetcher";
import { actionClient } from "@/lib/safe-action";
import { objToCamelCase } from "@/lib/utils";
import handleErrorResponse from "@/lib/handle-error-response";

/**
 * Fetches the store category data based on the provided slug.
 *
 * @param {string} slug - The slug identifier for the store category.
 * @returns {Promise<Object|null>} A promise that resolves to the JSON response containing the store category data,
 * or null if the response is not ok.
 */
export async function getStoreCategory(slug) {
  const response = await fetcher(
    `/api/store-ws/api/v1/store/homepage?menuSlug=${slug}`
  );
  if (!response.ok) return null;

  return response.json();
}

export async function getStore(params = {}) {
  const request = await fetcher("/api/store-ws/api/v2/store/homepage", {
    params,
  });
  if (!request.ok) return null;

  const response = await request.json();
  if (!response.success) return null;

  return objToCamelCase(response.data);
}

/**
 * Fetches the list of store categories.
 *
 * @returns {Promise<Object|null>} A promise that resolves to the JSON response containing the store categories,
 * or null if the response is not ok.
 */
export async function getStoreCategories() {
  const request = await fetcher(`/api/store-ws/api/v1/store/list`);
  if (!request.ok) return null;

  const response = await request.json();
  if (!response.success) return null;

  return objToCamelCase(response.data.menuList);
}

/**
 * Applies a coupon to a course purchase.
 *
 * @param {Object} params - The parameters for applying the coupon.
 * @param {number} params.courseId - The ID of the course.
 * @param {number} params.parentCourseId - The ID of the parent course.
 * @param {number} params.languageId - The ID of the language.
 * @param {number} params.packageId - The ID of the package.
 * @param {string} params.couponCode - The coupon code to apply.
 * @param {number} [params.paymentType=1] - The type of payment (default is 1).
 * @param {number} [params.action=1] - The action to perform (default is 1).
 * @returns {Promise<Object|null>} A promise that resolves to the JSON response containing the applied coupon data in camel case,
 * or null if the response is not ok or the coupon application is not successful.
 */
export async function applyCoupon({
  courseId,
  parentCourseId,
  languageId,
  packageId,
  couponCode,
  paymentType = 1,
  action = 1,
}) {
  const response = await fetcher("/api/store-ws/v1/buyNow/apply/coupon", {
    method: "POST",
    body: JSON.stringify({
      courseId,
      parentCourseId,
      packageId,
      languageId,
      couponCode,
      paymentType,
      action,
    }),
  });

  if (!response.ok) return null;

  const data = await response.json();
  if (data.success) return objToCamelCase(data.data);

  return null;
}

export const searchCourses = actionClient
  .inputSchema(
    z.object({
      page: z.number().describe("Page number"),
      pageSize: z.number().describe("Page size"),
      searchString: z.string().describe("Search Query"),
    })
  )
  .action(async ({ parsedInput }) => {
    const response = await fetcher("/api/store-ws/api/v3/store/search", {
      method: "POST",
      body: JSON.stringify(parsedInput),
    });

    if (!response.ok) return handleErrorResponse(response);

    const data = await response.json();
    if (data.success) return objToCamelCase(data.data);

    return null;
  });

/**
 * Server-side fetcher for exam preference categories.
 * Use this in RSC (Server Components) instead of the actionClient version.
 *
 * @param {Object} [params] - Optional query parameters.
 * @param {number} [params.goalId] - Goal Id.
 * @param {number} [params.languageId] - Language Id.
 * @param {number} [params.mode] - Mode (0 or 1).
 * @returns {Promise<Object[]|null>}
 */
export async function getExamPreferenceCategories(params) {
  const request = await fetcher(
    "/api/common-ws/api/v2/getExamPreferenceCategory",
    { params }
  );
  if (!request.ok) return null;

  const response = await request.json();
  if (!response.success) return null;

  return objToCamelCase(response.data);
}

export const fetchExamPreferenceCategories = actionClient
  .inputSchema(
    z
      .object({
        goalId: z.number().optional().describe("Goal Id"),
        languageId: z.number().optional().describe("Language Id"),
        mode: z.enum([0, 1]).default(0).describe("Language Id"),
      })
      .optional()
  )
  .action(async ({ clientInput }) => getExamPreferenceCategories(clientInput));

export const fetchGoalCoursesList = actionClient
  .inputSchema(
    z
      .object({
        goalId: z.number().describe("Goal Id"),
        categoryId: z.number().optional().describe("Category Id"),
        languageId: z.number().nullable().optional().describe("Language Id"),
      })
      .optional()
  )
  .action(async ({ clientInput }) => {
    const response = await fetcher("/api/store-ws/api/v1/pdp/course/list", {
      params: clientInput,
    });

    if (!response.ok) {
      return handleErrorResponse(response);
    }

    const data = await response.json();
    if (data.success) return objToCamelCase(data.data);

    return null;
  });

export async function getMenuSection(menuSlug) {
  const request = await fetcher("/api/common-ws/api/v1/getMenuSection", {
    params: { menuSlug },
  });
  if (!request.ok) return null;

  const response = await request.json();
  if (!response.success) return null;

  return objToCamelCase(response.data);
}
